import { Badge } from '@bennows/boows-react';
import { useTranslation } from 'react-i18next';
import { Figure, FigureGroup } from '../../components/Figures';
import { formatDate, formatDateTime, formatNumber } from '../../lib/format';
import { BILLING_TONES } from './billingTone';
import type { TenantDetail } from './useTenant';

const ROLES = ['admin', 'editor', 'reviewer', 'viewer'] as const;
const ACCOUNT_STATES = ['connected', 'expiring', 'expired', 'revoked'] as const;

/** Master data, members, connected accounts and the live billing state of one tenant. */
export function TenantOverviewTab({ tenant }: { readonly tenant: TenantDetail }) {
  const { t } = useTranslation();
  const { members, social_accounts: accounts, billing } = tenant.metrics;
  const admin = tenant.admin_contact;
  return (
    <>
      <FigureGroup title={t('tenant.master.title')}>
        <Figure
          wide
          text
          label={t('tenant.master.admin')}
          value={
            admin ? (
              <span className="ui-console-stacked">
                <span>{admin.name}</span>
                <a className="ui-console-muted" href={`mailto:${admin.email}`}>
                  {admin.email}
                </a>
              </span>
            ) : (
              t('tenant.master.noAdmin')
            )
          }
        />
        <Figure text label={t('tenant.master.timeZone')} value={tenant.time_zone} />
        <Figure text label={t('tenant.master.languages')} value={tenant.languages.map((code) => code.toUpperCase()).join(', ')} />
        <Figure text label={t('tenant.master.createdAt')} value={formatDate(tenant.created_at)} />
      </FigureGroup>
      <FigureGroup title={t('tenant.billing.title')} note={t('tenant.billing.note')}>
        <Figure text label={t('tenant.billing.state')} value={<Badge tone={BILLING_TONES[billing.state]}>{t(`billingStates.${billing.state}`)}</Badge>} />
        <Figure label={t('tenant.billing.balance')} value={billing.available === null ? t('tenant.billing.noBalance') : formatNumber(billing.available)} />
      </FigureGroup>
      <FigureGroup title={t('tenant.members.title')}>
        {ROLES.map((role) => (
          <Figure key={role} label={t(`tenant.members.${role}`)} value={formatNumber(members[role])} />
        ))}
        <Figure text label={t('tenant.members.lastSignIn')} value={members.last_sign_in_at ? formatDateTime(members.last_sign_in_at) : t('tenant.members.never')} />
        <Figure text label={t('tenant.members.lastActivity')} value={members.last_activity_at ? formatDateTime(members.last_activity_at) : t('tenant.members.never')} />
      </FigureGroup>
      <FigureGroup title={t('tenant.accounts.title')}>
        {ACCOUNT_STATES.map((state) => (
          <Figure key={state} label={t(`tenant.accounts.${state}`)} value={formatNumber(accounts[state])} />
        ))}
      </FigureGroup>
    </>
  );
}
